package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.logic.NavigatorAddBag
import com.example.mystybag.appCore.logic.NavigatorMerchantLocationsList
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep7
import com.example.mystybag.appCore.model.pages.MerchantLocationsListUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_CREATE_BAGS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.login.logic.NavigatorAddMerchantLocationFromDashboard
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantLocationsRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.MerchantLocationResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.toLegacyWhatYouCouldGetString
import kotlinx.coroutines.launch

class MerchantLocationsListViewModel(
    context: AppContext
) : ChildViewModel<MerchantLocationsListUIState>(context, MerchantLocationsListUIState()) {

    override fun onCreate() {
        navControls.title = sharedString(SR.strings.objects)
        navControls.backButtonVisible = false
    }

    override fun onResume() {
        // Initial load and refresh when the tab becomes visible (onResume follows onCreate on first open)
        loadLocations()
    }

    fun loadLocations() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        val isStaff = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
        if (merchantId == 0L) {
            return
        }

        scope.launch {
            updateState { it.copy(isLoading = true, isStaff = isStaff) }

            commandBlock(showProgress = false, hideProgress = false) {
                CommonCommands.getMerchantLocations(
                    GetMerchantLocationsRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantLocationsResponseModel
                                var locations = responseModel?.locations ?: emptyList()
                                val staffLocationIds = if (isStaff) {
                                    Preferences.getString(key = PREFERENCES_STAFF_LOCATION_IDS, "")
                                        .split(",").mapNotNull { it.trim().toLongOrNull() }.filter { it > 0L }
                                } else emptyList()
                                if (staffLocationIds.isNotEmpty()) {
                                    locations = locations.filter { it.id in staffLocationIds }
                                }
                                updateState {
                                    it.copy(
                                        isLoading = false,
                                        isStaff = isStaff,
                                        locations = locations,
                                        ratingsByLocation = responseModel?.ratingsByLocation?.mapKeys { it.key.toLongOrNull() ?: 0L }?.filterKeys { it != 0L } ?: emptyMap()
                                    )
                                }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false, isStaff = isStaff) }
                            }
                        }
                    )
            }
        }
    }

    /** Edit location: open the same multi-step flow as create, with data pre-filled (no intermediate loading screen). */
    fun onLocationClick(location: MerchantLocationResponseModel) {
        if (uiState.value.isStaff) return
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) return
        val step7Config = NavigatorMerchantRegistrationStep7(
            merchantId = merchantId,
            storeName = location.title ?: "",
            storeAddress = location.address ?: "",
            cityId = location.cityId.takeIf { it > 0L },
            cityName = location.cityName ?: "",
            storePhoneNumber = location.phoneNumber,
            dialCode = location.dialCode,
            packaging = location.packaging ?: "",
            additionalPickupInstructions = location.additionalPickupInstructions ?: "",
            surpriseBagContainsBuffetFood = location.surpriseBagContainsBuffetFood,
            returnToDashboard = false,
            locationId = location.id,
            initialLatitude = location.latitude.toDouble().takeUnless { location.latitude == -1f },
            initialLongitude = location.longitude.toDouble().takeUnless { location.longitude == -1f },
            whatYouCouldGet = location.products.toLegacyWhatYouCouldGetString(),
            businessCategoryId = location.businessCategoryId,
            existingCoverPhotoUrl = location.coverPhotoUrl?.takeIf { it.isNotBlank() },
            existingMenuPhotoUrls = location.menuPhotoUrls ?: emptyList()
        )
        navigate(step7Config)
    }

    fun onAddLocationClick() {
        // Locations are owner-only; staff (any role variation) is never allowed to create them.
        if (uiState.value.isStaff) return
        navigate(NavigatorAddMerchantLocationFromDashboard(merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)))
    }

    fun onAddBagClick(location: MerchantLocationResponseModel) {
        // Owner can always add bags; staff is gated by the canCreateBags permission.
        val isStaff = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
        if (isStaff && !Preferences.getBoolean(key = PREFERENCES_STAFF_CAN_CREATE_BAGS, false)) return
        navigate(
            NavigatorAddBag(
                merchantLocationId = location.id,
                businessCategoryId = location.businessCategoryId,
                bagId = null
            )
        )
    }

    fun onBackClick() {
        goBack()
    }
}
